import { isFeatureFlagEnabled } from '@/plugins/growthbook/growthbook';

const fetchingPropositions = state => state.fetchingPropositions;
const propositions = state => state.propositions;
const loadingPropositions = state => state.loading;
const primaryPropositionId = state =>
  state.primaryProposition ? state.primaryProposition.propositionId : null;
const primaryProposition = state => state.primaryProposition;

const primaryPropositionActive = state => {
  if (!state.propositions || state.propositions.length === 0) return false;

  const hasActive = state.propositions.some(
    p =>
      p &&
      p.active &&
      state.primaryProposition &&
      p.propositionId == state.primaryProposition.propositionId
  );

  return hasActive;
};

const isFreemiumAvailable = state => {
  if (!state.primaryProposition) return false;
  return state.primaryProposition.freemiumAvailable;
};

// Blur-address experiment: freemium users see approximate locations only —
// street numbers become a blurred pill and map pins become ~200 m circles.
// Single source for the gate; every surface must go through this getter.
//
// `rootState.app.ready` is load-bearing, not a convenience guard: GrowthBook
// is a plain module singleton that Vue does not track, so it cannot
// invalidate this getter on its own. app/initAppData commits
// setPrimaryProposition BEFORE setGrowthbookAttributes, meaning a read in
// that window would otherwise memoize `false` for the whole session. `ready`
// flips after both, giving the getter a reactive dep that changes once
// GrowthBook is live. Same pattern as app/getters.js isTestEnabled.
const hideExactAddress = (state, getters, rootState) =>
  !!rootState?.app?.ready &&
  !!state.primaryProposition?.isFreemium &&
  isFeatureFlagEnabled('blur-address');

const primaryPropositionInTrial = state => {
  if (!state.primaryProposition) return false;
  return state.primaryProposition.isTrial;
};

const primaryPropositionIsLimited = state => {
  if (!state.primaryProposition) return false;

  return (
    state.primaryProposition.isFreemium || state.primaryProposition.isTrial
  );
};

// AB test 'lock-all-chats': every chat is locked for freemium accounts
// (the behavior from before freemium could answer chats). Only evaluate
// the flag for freemium users so premium/trial never enter the experiment.
const lockAllFreemiumChats = (state, getters, rootState, rootGetters) => {
  if (!state.primaryProposition || !state.primaryProposition.isFreemium) {
    return false;
  }

  return rootGetters['app/isTestEnabled']('lock-all-chats');
};

// Free-area listings can pay; only the free product (1000) is not premium.
const primaryPropositionIsPremium = state => {
  if (!state.primaryProposition) return false;
  return (
    state.primaryProposition.productId != 1000 &&
    !state.primaryProposition.isTrial &&
    !state.primaryProposition.isFreemium
  );
};

const primaryPropositionIsPremiumOrFree = state => {
  if (!state.primaryProposition) return false;
  return (
    !state.primaryProposition.isTrial && !state.primaryProposition.isFreemium
  );
};

const accountType = state => {
  if (!state.propositions) {
    return 'unknown';
  }

  if (
    state.propositions.some(
      x => x.productId != 1000 && x.productId != 2000 && !x.isTrial
    )
  ) {
    return 'paying';
  } else if (state.propositions.some(x => x.isTrial)) {
    return 'trial';
  } else {
    return 'free';
  }
};

const primaryPropositionSubscriptionType = state => {
  if (!state.primaryProposition || !state.primaryProposition.propositionId) {
    return '';
  }

  const pp = state.primaryProposition;

  if (pp.productId == 2000) return 'freemium';
  else if (pp.productId == 1000) return 'free';
  else if (pp.isTrial) return 'trial';
  else return 'paying';
};

const totalImageCount = state => {
  if (!state.propositions) return 0;

  const imageCountArray = state.propositions
    .map(x => x.residences.map(y => y.images.length).flat())
    .flat()
    .filter(x => x);
  let imagesCount = 0;
  imageCountArray.forEach(x => (imagesCount += x));

  return imagesCount;
};

const blurGalleryImages = (state, getters, rootState, rootGetters) => {
  const isAuthenticated = rootGetters['app/isAuthenticated'];
  const allowAccess = rootGetters['app/allowAccess'];
  if (allowAccess) {
    return false;
  }

  if (!isAuthenticated) {
    return true;
  }

  if (
    isAuthenticated &&
    state.primaryProposition &&
    state.primaryProposition.residences &&
    state.primaryProposition.residences[0]
  ) {
    const imageCount = state.primaryProposition.residences[0].images.length;
    if (imageCount >= 2) {
      return false;
    }
  }

  return true;
};

const currentlyEditingProposition = state => state.currentlyEditingProposition;

export default {
  propositions,
  loadingPropositions,
  accountType,
  totalImageCount,
  primaryPropositionId,
  primaryProposition,
  primaryPropositionActive,
  fetchingPropositions,
  isFreemiumAvailable,
  hideExactAddress,
  primaryPropositionInTrial,
  primaryPropositionIsLimited,
  lockAllFreemiumChats,
  primaryPropositionIsPremium,
  primaryPropositionIsPremiumOrFree,
  primaryPropositionSubscriptionType,
  blurGalleryImages,
  currentlyEditingProposition
};
